import { useCallback, useMemo } from "react";
import { useModal } from "modal-react-ccs";

import { useLanguage, useRest } from "./";

import { CustomObject } from "../types";

type ModalPassword = {
    show: () => void;
};

export const useModalPassword = (): ModalPassword => {
    const { t, language } = useLanguage();
    const { consume } = useRest();
    const { showCustom, getContentRef } = useModal();

    const labels = useMemo(
        () => ({
            changePassword: t("Change Password"),
            passwordCurrent: t("Password Current"),
            passwordNew: t("Password New"),
            passwordConfirmation: t("Password Confirm"),
            passwordConfirmDontMatch: t("Password Confirm Dont Match"),
            passwordCurrentDontMatch: t("Password Current Dont Match"),
            passwordChanged: t("Password changed"),
            confirm: t("Confirm"),
            checkTheErrorsInTheFields: t("Check the errors in the fields"),
        }),
        [language]
    );

    const handleOnClickChangePassword = async () => {
        const modalRef = await getContentRef();

        modalRef?.clearMessages("success");
        modalRef?.clearMessages("error");

        if (modalRef?.serializer) {
            const serializer = (await modalRef.serializer()) as CustomObject;

            const data = serializer[0];
            const hasErrors = serializer[1] && Object.keys(serializer[1]).length > 0;

            const _validate = await validate(data, hasErrors);

            if (_validate && data) {
                await modalRef?.clearMessages("error");

                try {
                    const response = await consume("put", "/profile", {
                        user_old_password: data.user_password_current,
                        user_password: data.user_password_new,
                        user_password_confirmation: data.user_password_new,
                    });

                    if (response) {
                        modalRef?.addMessage("success", {
                            item: labels.passwordChanged,
                        });
                    }
                } catch (error) {
                    const messageError = error as unknown as string;

                    if (messageError) {
                        modalRef?.addMessage("error", {
                            size: "mini",
                            list: [messageError],
                        });
                    } else {
                        console.error(error);
                    }
                }
            }
        }
    };

    const validate = useCallback(
        async (data: CustomObject, hasError: boolean) => {
            const errors: string[] = [];

            const modalRef = await getContentRef();

            const { user_password_new, user_password_confirmation } = data;

            if (hasError) {
                errors.push(labels.checkTheErrorsInTheFields);
            }

            if (user_password_confirmation !== user_password_new) {
                errors.push(labels.passwordConfirmDontMatch);

                //TODO essa validacao de senha pode estar na framework
                modalRef
                    ?.getWidget("user_password_confirmation")
                    ?.setError(true);
            }

            if (errors.length > 0) {
                modalRef?.addMessage("error", {
                    size: "mini",
                    list: errors,
                });

                return false;
            } else {
                return true;
            }
        },
        [labels]
    );

    const show = () => {
        showCustom({
            size: "mini",
            header: {
                icon: "key",
                text: labels.changePassword,
            },
            actions: [
                {
                    content: labels.confirm,
                    onClick: handleOnClickChangePassword,
                },
            ],
            content: {
                rows: [
                    {
                        columns: [
                            {
                                widget: {
                                    id: "user_password_current",
                                    field: {
                                        type: "password",
                                        placeholder: labels.passwordCurrent,
                                        validatePassword: false,
                                        hasPassValidator: false,
                                        required: true,
                                    },
                                },
                            },
                            {
                                widget: {
                                    id: "user_password_new",
                                    field: {
                                        type: "password",
                                        placeholder: labels.passwordNew,
                                        validatePassword: true,
                                        hasPassValidator: true,
                                        required: true,
                                    },
                                },
                            },
                            {
                                widget: {
                                    id: "user_password_confirmation",
                                    field: {
                                        type: "password",
                                        placeholder:
                                            labels.passwordConfirmation,
                                        validatePassword: false,
                                        hasPassValidator: false,
                                        required: true,
                                    },
                                },
                            },
                        ],
                    },
                ],
            },
        });
        return;
    };

    return {
        show,
    };
};
